/**
 * デザイントークンシステム
 * Atlassian Design Systemの思想に基づいたトークン設計
 */

// カラーパレット - 参考サイトから抽出したモダンなカラーシステム
export const colors = {
  primary: {
    50: "#e3f2fd",
    100: "#bbdefb",
    200: "#90caf9",
    300: "#64b5f6",
    400: "#42a5f5",
    500: "#2196f3",
    600: "#1976d2", // コーポレートカラー
    700: "#1565c0",
    800: "#0d47a1",
    900: "#0a347a",
    950: "#042961",
  },
  secondary: {
    50: "#f0f9ff",
    100: "#e0f2fe",
    200: "#bae6fd",
    300: "#7dd3fc",
    400: "#38bdf8",
    500: "#0ea5e9",
    600: "#0284c7",
    700: "#0369a1",
    800: "#075985",
    900: "#0c4a6e",
  },
  neutral: {
    0: "#ffffff",
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
    950: "#020617",
  },
  accent: {
    cyan: "#22d3ee",
    emerald: "#10b981",
    violet: "#8b5cf6",
    rose: "#f43f5e",
    amber: "#f59e0b",
  },
  semantic: {
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#3b82f6",
  },
  brand: {
    lamact: "#1976d2",
    gradient: {
      primary: "linear-gradient(135deg, #1976d2 0%, #2196f3 100%)",
      secondary: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
      accent: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
    },
  },
} as const;

// スペーシングシステム（4pxベース）
export const spacing = {
  0: "0px",
  50: "2px",
  100: "4px",
  150: "6px",
  200: "8px",
  250: "10px",
  300: "12px",
  400: "16px",
  500: "20px",
  600: "24px",
  700: "28px",
  800: "32px",
  900: "36px",
  1000: "40px",
  1200: "48px",
  1600: "64px",
  2000: "80px",
  2400: "96px",
} as const;

// タイポグラフィ - モダンな日本語・欧文混植システム
export const typography = {
  fontFamily: {
    base: 'Inter, "Noto Sans JP", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif',
    display:
      'Inter, "Noto Sans JP", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: '"JetBrains Mono", "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    japanese:
      '"Noto Sans JP", "Hiragino Sans", "Hiragino Kaku Gothic ProN", "Arial Unicode MS", Meiryo, sans-serif',
  },
  fontSize: {
    // レスポンシブタイポグラフィ（clamp関数使用）
    xs: "clamp(0.75rem, 0.69rem + 0.31vw, 0.875rem)", // 12-14px
    sm: "clamp(0.875rem, 0.81rem + 0.31vw, 1rem)", // 14-16px
    base: "clamp(1rem, 0.94rem + 0.31vw, 1.125rem)", // 16-18px
    lg: "clamp(1.125rem, 1.06rem + 0.31vw, 1.25rem)", // 18-20px
    xl: "clamp(1.25rem, 1.19rem + 0.31vw, 1.5rem)", // 20-24px
    "2xl": "clamp(1.5rem, 1.38rem + 0.63vw, 1.875rem)", // 24-30px
    "3xl": "clamp(1.875rem, 1.69rem + 0.94vw, 2.5rem)", // 30-40px
    "4xl": "clamp(2.25rem, 1.94rem + 1.56vw, 3.25rem)", // 36-52px
    "5xl": "clamp(3rem, 2.56rem + 2.19vw, 4.375rem)", // 48-70px
    "6xl": "clamp(3.75rem, 3.19rem + 2.81vw, 5.625rem)", // 60-90px
  },
  fontWeight: {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },
  lineHeight: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
  letterSpacing: {
    tighter: "-0.05em",
    tight: "-0.025em",
    normal: "0em",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em",
  },
} as const;

// 境界線 - モダンな角丸システム
export const borderRadius = {
  none: "0px",
  xs: "2px",
  sm: "6px",
  base: "8px",
  md: "12px",
  lg: "16px",
  xl: "24px",
  "2xl": "32px",
  "3xl": "48px",
  full: "9999px",
} as const;

// シャドウシステム - 深度とコンテキストを表現
export const shadows = {
  none: "none",
  xs: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  sm: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  base: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  md: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  lg: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  xl: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  "2xl": "0 50px 100px -20px rgba(0, 0, 0, 0.25)",
  inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",

  // カラードシャドウ
  colored: {
    primary:
      "0 20px 25px -5px rgba(62, 100, 234, 0.2), 0 10px 10px -5px rgba(62, 100, 234, 0.1)",
    secondary:
      "0 20px 25px -5px rgba(14, 165, 233, 0.2), 0 10px 10px -5px rgba(14, 165, 233, 0.1)",
    success:
      "0 20px 25px -5px rgba(16, 185, 129, 0.2), 0 10px 10px -5px rgba(16, 185, 129, 0.1)",
    warning:
      "0 20px 25px -5px rgba(245, 158, 11, 0.2), 0 10px 10px -5px rgba(245, 158, 11, 0.1)",
    error:
      "0 20px 25px -5px rgba(239, 68, 68, 0.2), 0 10px 10px -5px rgba(239, 68, 68, 0.1)",
  },
} as const;

// レイアウト用のスペーシング
export const layout = {
  overlap: {
    small: "-10vh",
    medium: "-20vh",
    large: "-30vh",
  },
} as const;

// ブレークポイント
export const breakpoints = {
  xs: "0px",
  sm: "600px",
  md: "900px",
  lg: "1200px",
  xl: "1536px",
} as const;

// z-index管理
export const zIndex = {
  hide: -1,
  auto: "auto",
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  header: 1150,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
} as const;

// アニメーション・トランジション
export const animation = {
  duration: {
    fast: "150ms",
    base: "200ms",
    slow: "300ms",
    slower: "500ms",
    slowest: "1000ms",
  },
  easing: {
    linear: "linear",
    ease: "ease",
    easeIn: "cubic-bezier(0.4, 0, 1, 1)",
    easeOut: "cubic-bezier(0, 0, 0.2, 1)",
    easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    bounce: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
    spring: "cubic-bezier(0.175, 0.885, 0.32, 1.275)",
  },
  keyframes: {
    fadeIn: `
      from { opacity: 0; }
      to { opacity: 1; }
    `,
    fadeInUp: `
      from { 
        opacity: 0; 
        transform: translateY(30px); 
      }
      to { 
        opacity: 1; 
        transform: translateY(0); 
      }
    `,
    slideInRight: `
      from { 
        opacity: 0; 
        transform: translateX(100px); 
      }
      to { 
        opacity: 1; 
        transform: translateX(0); 
      }
    `,
    scaleIn: `
      from { 
        opacity: 0; 
        transform: scale(0.95); 
      }
      to { 
        opacity: 1; 
        transform: scale(1); 
      }
    `,
  },
} as const;

// グリッドシステム
export const grid = {
  container: {
    xs: "100%",
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
  columns: 12,
  gap: {
    xs: spacing[300],
    sm: spacing[400],
    md: spacing[600],
    lg: spacing[800],
    xl: spacing[1000],
  },
} as const;
